/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
} from 'react';
import { useOrientation, usePrevious } from 'react-use';
import { getInitialUrlArgs, isDebugFeature } from './UrlArgsProvider';
import {useTheme} from 'styled-components';
import { useMediaQuery } from "@react-hook/media-query"

export type FrameContextValue = {
    allowFullscreen: boolean;
    orientation: string;
    isFullscreen: boolean;
	fullscreenPreferred: boolean;
    enterFullScreen: () => void;
    exitFullScreen: () => void;
};

const defaultFrameContextValue: FrameContextValue = {
    allowFullscreen: false,
    orientation: '',
    isFullscreen: false,
    fullscreenPreferred: false,
    enterFullScreen: () => {
        console.error('Uninitialised enterFullScreen function');
    },
    exitFullScreen: () => {
        console.error('Uninitialised enterFullScreen function');
    },
};

const { debug } = getInitialUrlArgs();
const VERBOSE = debug || isDebugFeature('FrameStateProvider');

const FrameStateContext = createContext<FrameContextValue>(
    defaultFrameContextValue
);

// eslint-disable-next-line react-refresh/only-export-components
export const useFrameState = () => useContext(FrameStateContext);

export const FrameStateProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const fullScreenRef = useRef(document.body);
    const [fullscreenElement, setFullscreenElement] = useState<Element | null>(
        null
    );
	const theme = useTheme();

	const isFullScreenLayout = useMediaQuery(theme.breakpoints.fullScreenStream);
	
    const { type: orientation } = useOrientation();
    const previousOrientation = usePrevious(orientation);

    useEffect(() => {
        VERBOSE && console.log(`[FrameStateProvider] listening`);

        const handleFullscreenChange = () => {
            setFullscreenElement(document.fullscreenElement);
            VERBOSE &&
                console.log(
                    `[FrameStateProvider] handleFullscreenChange => `,
                    document.fullscreenElement ? 'Enabled' : 'Disabled'
                );
        };

        const handleOrientationChange = () => {
            VERBOSE &&
                console.log(`[FrameStateProvider] deviceorientation event`);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        window.addEventListener('deviceorientation', handleOrientationChange);
        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            );
            document.removeEventListener(
                'deviceorientation',
                handleOrientationChange
            );
        };
    }, []);

    const enterFullScreen = useCallback((onRotation = false) => {
        (async () => {
            VERBOSE &&
                console.log(`[FrameStateProvider] enterFullScreen requested`);
            if (!document.fullscreenElement && fullScreenRef.current) {
                try {
                    await fullScreenRef.current.requestFullscreen({
                        navigationUI: 'hide',
                    });
                    VERBOSE &&
                        console.log(
                            `[FrameStateProvider] enterFullScreen SUCCESS`
                        );
                } catch (err) {
                    console[onRotation ? 'log' : 'error'](
                        `[FrameStateProvider] enterFullScreen FAILED`,
                        err
                    );
                }
                try {
					if (Object.hasOwnProperty.call(screen.orientation, 'lock')) {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						await (screen.orientation as any).lock('landscape');
						VERBOSE &&
                        console.log(
                            `[FrameStateProvider] screen.orientation.lock SUCCESS`
                        );						
					} else {
						VERBOSE &&
                        console.log(
                            `[FrameStateProvider] screen.orientation.lock NOT AVAILABLE`
                        );						

					}
                } catch (err) {
                    VERBOSE &&console.error(
                        `[FrameStateProvider] screen.orientation.lock FAILED`,
                        err
                    );
                }

			}
        })();
    }, []);

    const exitFullScreen = useCallback(() => {
        document.exitFullscreen();
        screen.orientation.unlock();
        VERBOSE && console.log(`[FrameStateProvider] exitFullScreen`);
    }, []);

    const allowFullscreen = Boolean(document.fullscreenEnabled);
    const isFullscreen = fullscreenElement !== null;
    const fullscreenPreferred = isFullScreenLayout;

    if (previousOrientation && orientation !== previousOrientation) {
        VERBOSE &&
            console.log(
                `[FrameStateProvider] Orientation Change ${orientation}`
            );
        // NOTE: Uncomment this to exit fullscreen when rotating back to portrait mode
        // if (isFullscreen && orientation==='portrait-primary')
        // 	exitFullScreen();
        if (!isFullscreen && orientation === 'landscape-primary')
            enterFullScreen(true);
    }

    const value = useMemo(
        () => ({
            allowFullscreen,
            orientation,
            fullscreenPreferred,
            isFullscreen,
            enterFullScreen: () => enterFullScreen(false),
            exitFullScreen,
        }),
        [
            orientation,
            fullscreenPreferred,
            isFullscreen,
            allowFullscreen,
            enterFullScreen,
            exitFullScreen,
        ]
    );

    VERBOSE &&
        console.log(`[FrameStateProvider] update:`, {
            allowFullscreen,
            orientation,
            fullscreenPreferred,
            isFullscreen,
        });

    return (
        <FrameStateContext.Provider value={value}>
            {children}
        </FrameStateContext.Provider>
    );
};
